import { useRef, useState, type FormEvent, type RefObject } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ArrowUp, Camera, Star } from "lucide-react";
import { CATEGORY_COLORS, de, type Product } from "@ainkauf/core";
import { suggestions } from "@/db/repository";
import { FavoritesRow } from "@/components/FavoritesRow";

/**
 * Die Eingabe: ein einziges weiches Feld, in dem Kamera und Absenden sitzen.
 *
 * Vorher waren es drei nebeneinanderliegende Kästen, das sah nach Formular
 * aus. Jetzt ist es eine Zeile, in die man hineinschreibt. Enter legt an, der
 * Fokus bleibt, damit mehrere Artikel hintereinander gehen.
 */
export function QuickAdd({
  onAdd,
  onPhoto,
  feldRef,
}: {
  onAdd: (raw: string) => Promise<void>;
  onPhoto?: (datei: File) => void;
  /** Von außen, damit der Knopf in der Kopfkarte das Feld noch im Tipp fokussieren kann. */
  feldRef?: RefObject<HTMLInputElement | null>;
}) {
  const [wert, setWert] = useState("");
  const eigenesFeld = useRef<HTMLInputElement>(null);
  const feld = feldRef ?? eigenesFeld;

  const vorschlaege = useLiveQuery(
    async () => (wert.trim().length === 0 ? [] : suggestions(wert)),
    [wert],
    [] as Product[],
  );

  async function anlegen(text: string) {
    const eingabe = text.trim();
    if (eingabe.length === 0) return;
    setWert("");
    await onAdd(eingabe);
    feld.current?.focus();
  }

  return (
    <div className="flex flex-col gap-2">
      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void anlegen(wert);
        }}
        className="karte flex items-center gap-1 py-1.5 pl-4 pr-1.5"
      >
        <input
          ref={feld}
          value={wert}
          onChange={(event) => {
            setWert(event.target.value);
          }}
          type="text"
          inputMode="text"
          autoComplete="off"
          enterKeyHint="done"
          aria-label={de.items.quickAddPlaceholder}
          placeholder={de.items.quickAddPlaceholder}
          data-testid="quick-add-input"
          className="min-h-[44px] min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-ink-3"
        />

        {onPhoto && wert.trim().length === 0 && (
          <label
            className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-ink-3 transition-transform active:scale-90"
            aria-label={de.assistant.photoNote}
          >
            <Camera size={20} strokeWidth={1.75} aria-hidden />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="assistant-photo"
              className="sr-only"
              onChange={(event) => {
                const datei = event.target.files?.[0];
                if (datei) onPhoto(datei);
              }}
            />
          </label>
        )}

        <button
          type="submit"
          aria-label={de.lists.create}
          data-testid="quick-add-submit"
          className={`inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-all duration-200 ${
            wert.trim().length === 0 ? "bg-paper-2 text-ink-3" : "knopf-haupt"
          }`}
        >
          <ArrowUp size={20} strokeWidth={2.5} aria-hidden />
        </button>
      </form>

      {wert.trim().length === 0 && <FavoritesRow onAdd={(name) => anlegen(name)} />}

      {vorschlaege.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="suggestions">
          {vorschlaege.map((produkt) => {
            const farbe = produkt.category ? `var(--food-${CATEGORY_COLORS[produkt.category]})` : "var(--paper-3)";
            return (
              <li key={produkt.id}>
                <button
                  type="button"
                  onClick={() => void anlegen(produkt.name)}
                  data-testid="suggestion"
                  data-name={produkt.name}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-card px-4 text-ink shadow-[var(--shadow-card)] transition-transform active:scale-95"
                >
                  <span aria-hidden className="size-2.5 rounded-full" style={{ background: farbe }} />
                  {produkt.is_favorite && <Star size={13} strokeWidth={2.5} className="text-honey" aria-hidden />}
                  {produkt.name}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
